import { Box, FormControl } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import { useEffect, useState } from 'react';

import moment, { Moment } from 'moment';

import styles from './DatePicker.module.scss';

const { borderColorLight, textColor } = styles;

type IDatepickerComponent = {
	onChange?: (val: string) => void;
	disabled?: boolean;
	open?: boolean;
	dateValue?: string;
	mode?: 'standard' | 'filled';
	propType?: any;
	showLabel?: boolean;
	readOnly?: boolean;
};

export const displayDateFormat = 'MM/DD/YYYY';
const DatepickerComponent = (props: IDatepickerComponent) => {
	const {
		onChange,
		open,
		dateValue,
		propType,
		mode = 'filled',
		showLabel = true,
		readOnly,
	} = props;

	const [value, setValue] = useState<Moment | null>();

	useEffect(() => {
		const selDateValue =
			dateValue !== undefined && dateValue !== '' ? moment(dateValue) : null;
		setValue(selDateValue);
	}, [dateValue]);

	const [openDialog, setDialog] = useState(open);

	const onDateChange = (val: any) => {
		if (onChange) {
			setValue(val);
			if (val && val?.isValid()) {
				onChange(val.format(displayDateFormat));
			} else {
				onChange('');
			}
			setDialog(false);
		}
	};

	return (
		<LocalizationProvider
			dateAdapter={AdapterMoment}
			// localeText={
			//   deDE.components.MuiLocalizationProvider.defaultProps.localeText
			// }
		>
			{mode === 'filled' ? (
				<DatePicker
					{...propType}
					open={openDialog}
					value={value || null}
					onChange={(e) => onDateChange(e)}
					slotProps={{
						popper: { placement: 'bottom' },
						textField: {
							variant: 'filled',
							InputLabelProps: { shrink: true },
							required: !!propType?.required,
							inputProps: {
								readOnly, // Add this line to make the input read-only
								...propType?.inputProps,
							},
						},
					}}
					format={displayDateFormat}
					sx={{
						'&.MuiPickersPopper-root': {
							border: '0px',
							transform: 'translate(0%, 0%)',
						},
						'& .MuiInputLabel-root': {
							overflow: 'hidden',
							borderRadius: '4px',
							color: textColor,
							'&:hover': {
								backgroundColor: 'transparent',
							},
						},

						'& .MuiFilledInput-root': {
							overflow: 'hidden',
							borderRadius: '4px',
							backgroundColor: 'white',
							border: '1px solid',
							paddingLeft: '4px',
							borderColor: borderColorLight,
							'&:hover': {
								backgroundColor: 'transparent',
							},
							'&.Mui-focused': {
								backgroundColor: 'transparent',
								borderColor: borderColorLight,
							},
							'&.Mui-error': {
								borderColor: borderColorLight,
								borderBottomColor: borderColorLight,
							},
							'&.Mui-error:after': {
								borderColor: borderColorLight,
								borderBottomColor: borderColorLight,
							},
							'&.Mui-error:before': {
								borderColor: borderColorLight,
								borderBottomColor: borderColorLight,
							},
						},
						'& .MuiFormLabel-asterisk': {
							color: 'red',
						},
					}}
				/>
			) : (
				<Box className={styles['select-wrapper']}>
					{showLabel && (
						<Box className={styles['select-label']}>
							{propType.label}
							{propType.required ? (
								<span className={styles['select-label-asterisk']}> *</span>
							) : null}{' '}
							:
						</Box>
					)}
					<FormControl sx={{ width: '100%', pr: 1 }}>
						<DatePicker
							{...propType}
							open={openDialog}
							value={value || null}
							onChange={(e) => onDateChange(e)}
							inputProps={{
								'aria-label': 'Without label',
								'data-testid': 'date-picker',
							}}
							label=""
							slotProps={{
								popper: { placement: 'bottom' },
								textField: {
									variant: 'standard',
									required: !!propType?.required,
									inputProps: {
										readOnly: propType.readOnly, // Add this line to make the input read-only
										...propType?.inputProps,
									},
								},
							}}
							format={displayDateFormat}
							sx={{
								'&.MuiPickersPopper-root': {
									border: '0px',
									transform: 'translate(0%, 0%)',
								},
								'& .MuiInputLabel-root': {
									overflow: 'hidden',
									borderRadius: '4px',
									color: textColor,
									'&:hover': {
										backgroundColor: 'transparent',
									},
								},
								'& .MuiInputBase-root': {
									overflow: 'hidden',
									borderRadius: '4px',
									backgroundColor: 'white',
									border: '0px solid',
									paddingLeft: '4px',
									borderColor: borderColorLight,
									'&:before': {
										borderBottom: 0,
									},
									'&:after': {
										borderBottom: 0,
									},
									'&:hover': {
										borderBottom: 0,
										backgroundColor: 'transparent',
									},
									'&:hover:before': {
										borderBottom: 0,
										backgroundColor: 'transparent',
									},
									'&:hover:not(.Mui-disabled, .Mui-error):before': {
										borderBottom: 0,
										backgroundColor: 'transparent',
									},
									'&.Mui-focused': {
										borderBottom: 0,
										backgroundColor: 'transparent',
										borderColor: borderColorLight,
									},
									'&.Mui-error': {
										borderColor: borderColorLight,
										borderBottomColor: borderColorLight,
									},
									'&.Mui-error:after': {
										borderColor: borderColorLight,
										borderBottomColor: borderColorLight,
									},
									'&.Mui-error:before': {
										borderColor: borderColorLight,
										borderBottomColor: borderColorLight,
									},
								},
								'& .MuiFormLabel-asterisk': {
									color: 'red',
								},
							}}
						/>
					</FormControl>
				</Box>
			)}
		</LocalizationProvider>
	);
};

export default DatepickerComponent;
